<template>
    <div>
        <h3 class="pb-4 text-lg font-medium leading-6 text-left no-underline normal-case non-italic">{{ title || 'Personal Information' }}</h3>

        <div class="space-y-4">
            <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div class="col-span-1">
                    <select-field v-model="modelValue.salutation" label="Salutation" track-by="value" label-by="label" :options="salutations" :required="true"></select-field>
                </div>

                <div class="col-span-1">
                    <text-field v-model="modelValue.firstName" label="Legal First Name" :required="true"></text-field>
                </div>

                <div class="col-span-1">
                    <text-field v-model="modelValue.lastName" label="Last Name" :required="true"></text-field>
                </div>

                <div class="col-span-1">
                    <text-field v-model="modelValue.middleInitial" v-maska="'S'" label="Middle Initial" class="uppercase" :required="false"></text-field>
                </div>

                <div class="col-span-1">
                    <text-field v-model="modelValue.suffix" label="Suffix" :required="false"></text-field>
                </div>

                <div class="col-span-1">
                    <div class="relative">
                        <div class="absolute top-0.5 left-16 ml-2 group">
                            <svg class="w-4 h-4 text-gray-300 hover:text-gray-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"></path></svg>
                            <div class="absolute hidden w-64 p-4 mb-1 -ml-4 text-xs font-semibold text-white bg-black rounded-md group-hover:block bottom-full left-full">
                                Please let us know which name you prefer to go by.
                            </div>
                        </div>
                        <text-field v-model="modelValue.nickname" label="Nickname" :required="false"></text-field>
                    </div>
                </div>

                <div class="col-span-1">
                    <select-field v-model="modelValue.maritalStatus" label="Marital Status" :options="['Single', 'Married']" :searchable="false" :required="true"></select-field>
                </div>

                <div class="col-span-1">
                    <text-field v-model="modelValue.mobilePhone" pattern="[0-9]*" inputmode="numeric" v-maska="'+1 ### ### ####'" label="Mobile Number" :required="true"></text-field>
                </div>

                <div class="col-span-1">
                    <text-field v-model="modelValue.phone" pattern="[0-9]*" inputmode="numeric" v-maska="'(###) ###-####'" label="Other Phone Number" :required="false"></text-field>
                </div>

                <div v-show="showEmail" class="col-span-1">
                    <text-field v-model="modelValue.email" label="Email Address" :required="true"></text-field>
                </div>
            </div>

            <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div v-if="isSpouse" class="col-span-1">
                    <checkbox-field v-model="sameAsAbove">Same as above</checkbox-field>
                </div>

                <div class="col-span-1 sm:col-span-3">
                    <address-field v-model="modelValue.address1" @placechanged="onPlaceChanged" label="Address Line 1" :required="true"></address-field>
                </div>

                <div class="col-span-1 sm:col-span-3">
                    <text-field v-model="modelValue.address2" label="Address Line 2" :required="false"></text-field>
                </div>

                <div class="col-span-1">
                    <text-field v-model="modelValue.city" label="City" :required="true"></text-field>
                </div>

                <div class="col-span-1">
                    <template v-if="modelValue.country == 'US'">
                        <select-field v-model="modelValue.state" label="State" label-by="name" track-by="abbreviation" :options="states" :searchable="true" :required="true"></select-field>
                    </template>
                    <template v-else>
                        <text-field v-model="modelValue.state" label="State" :required="true"></text-field>
                    </template>
                </div>

                <div class="col-span-1">
                    <text-field v-model="modelValue.zip" pattern="[0-9]*" inputmode="numeric" v-maska="'#*'" label="Zip Code" :required="true"></text-field>
                </div>
            </div>

            <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div class="col-span-1">
                    <select-field v-model="modelValue.country" label="Country" label-by="country" track-by="abbreviation" :options="countries" :searchable="true" :required="true"></select-field>
                </div>

                <div class="col-span-1">
                    <select-field v-model="modelValue.employment" label="Select Employment Status" :options="['Employed', 'Not Employed', 'Retired', 'Other']" :required="true"></select-field>
                </div>
            </div>

            <div v-if="modelValue.employment == 'Employed'" class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div class="col-span-1">
                    <text-field v-model="modelValue.occupation" label="Occupation" :required="true"></text-field>
                </div>

                <div class="col-span-1">
                    <text-field v-model="modelValue.employer" label="Employer" :required="true"></text-field>
                </div>
            </div>

            <div v-if="modelValue.employment == 'Other'" class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div class="col-span-1">
                    <text-field v-model="modelValue.empOther" label="If “Other”, please describe:" :required="true"></text-field>
                </div>
            </div>
        </div>

        <h3 class="pb-4 mt-12 text-lg font-medium leading-6 text-left no-underline normal-case non-italic">USA Patriot Act Information (required by Federal Law)</h3>

        <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div class="col-span-1">
                <select-field v-model="modelValue.citizenship" label="Citizenship" :options="['U.S. Citizen', 'U.S. Resident', 'Non-Resident']" :required="true"></select-field>
            </div>

            <div class="col-span-1">
                <text-field v-model="modelValue.dob" pattern="[0-9]*" inputmode="numeric" v-maska="'##/##/####'" label="Date of Birth" placeholder="MM/DD/YYYY" :required="true"></text-field>
            </div>

            <div class="col-span-1 sm:col-span-3">
                <label class="block text-sm font-medium text-gray-700">ID Type<span class="font-bold text-teal">*</span></label>
                <div class="flex flex-col mt-4 space-y-4 sm:items-center sm:space-x-8 sm:flex-row sm:space-y-0">
                    <radio-field v-model="modelValue.idType" value="DriversLicense">Driver's License</radio-field>
                    <radio-field v-model="modelValue.idType" value="Passport">Passport</radio-field>
                    <radio-field v-model="modelValue.idType" value="StateID">State ID</radio-field>
                    <radio-field v-model="modelValue.idType" value="OtherID">Other Govt ID</radio-field>
                </div>
            </div>

            <div class="col-span-1">
                <text-field v-model="modelValue.idNumber" label="ID Number" :required="true"></text-field>
            </div>

            <div class="col-span-1">
                <text-field v-model="modelValue.idExpiration" pattern="[0-9]*" inputmode="numeric" v-maska="'##/##/####'" label="Exp. Date" :required="true"></text-field>
            </div>

            <div class="col-span-1">
                <text-field v-model="modelValue.idIssuer" label="State/Issuer" :required="true"></text-field>
            </div>

            <div class="col-span-1">
                <text-field v-model="modelValue.ssn" pattern="[0-9]*" inputmode="numeric" v-maska="'###-##-####'" label="Social Security Number/Tax ID" :required="true"></text-field>
            </div>
        </div>

        <h3 class="pb-4 mt-12 text-lg font-medium leading-6 text-left no-underline normal-case non-italic">Industry  and Other Affiliation</h3>

        <div class="space-y-12">
            <div>
                <label class="block text-sm font-medium text-gray-700">Are you associated with a financial firm, investment company, bank, or regulator?</label>
                <div class="flex flex-col mt-4 space-y-4 sm:items-center sm:space-x-8 sm:flex-row sm:space-y-0">
                    <radio-field v-model="modelValue.financialAffiliation" value="Yes">Yes</radio-field>
                    <radio-field v-model="modelValue.financialAffiliation" value="No">No</radio-field>
                </div>

                <div v-show="modelValue.financialAffiliation == 'Yes'" class="mt-6">
                    <label class="block text-sm font-medium text-gray-700">If yes, please tell us about:</label>
                    <div class="grid grid-cols-1 gap-4 mt-4 sm:grid-cols-2">
                        <div class="col-span-1">
                            <text-field v-model="modelValue.financialFirm" label="Firm/Company Name" :required="true"></text-field>
                        </div>

                        <div class="col-span-1">
                            <text-field v-model="modelValue.financialFirmPosition" label="Your Position" :required="true"></text-field>
                        </div>
                    </div>
                </div>
            </div>

            <div>
                <label class="block text-sm font-medium text-gray-700">Are you an officer, director, or 10% or more owner of a publicly owned company?</label>
                <div class="flex flex-col mt-4 space-y-4 sm:items-center sm:space-x-8 sm:flex-row sm:space-y-0">
                    <radio-field v-model="modelValue.publicCompanyAffiliation" value="Yes">Yes</radio-field>
                    <radio-field v-model="modelValue.publicCompanyAffiliation" value="No">No</radio-field>
                </div>

                <div v-show="modelValue.publicCompanyAffiliation == 'Yes'" class="mt-6">
                    <label class="block text-sm font-medium text-gray-700">If yes, please tell us about:</label>
                    <div class="grid grid-cols-1 gap-4 mt-4 sm:grid-cols-2">
                        <div class="col-span-1">
                            <text-field v-model="modelValue.publicCompany" label="Firm/Company Name" :required="true"></text-field>
                        </div>

                        <div class="col-span-1">
                            <text-field v-model="modelValue.publicCompanyPosition" label="Your Position" :required="true"></text-field>
                        </div>
                    </div>
                </div>
            </div>

            <div>
                <label class="block text-sm font-medium text-gray-700">Are you or is anyone close to you a senior military, gov’t or political official in a non-US country?</label>
                <div class="flex flex-col mt-4 space-y-4 sm:items-center sm:space-x-8 sm:flex-row sm:space-y-0">
                    <radio-field v-model="modelValue.politicalAffiliation" value="Yes">Yes</radio-field>
                    <radio-field v-model="modelValue.politicalAffiliation" value="No">No</radio-field>
                </div>

                <div v-show="modelValue.politicalAffiliation == 'Yes'" class="mt-6">
                    <label class="block text-sm font-medium text-gray-700">If yes, please tell us about:</label>
                    <div class="grid grid-cols-1 gap-4 mt-4 sm:grid-cols-2">
                        <div class="col-span-1">
                            <select-field v-model="modelValue.politicalCountry" label="Country" label-by="country" track-by="abbreviation" :options="countries" :searchable="true" :required="true"></select-field>
                        </div>

                        <div class="col-span-1">
                            <text-field v-model="modelValue.politicalRole" label="Office/Role" :required="true"></text-field>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import TextField from '@Fields/Text'
import TextareaField from '@Fields/Textarea'
import SelectField from '@Fields/Select'
import CheckboxField from '@Fields/Checkbox'
import RadioField from '@Fields/Radio'
import DateField from '@Fields/Date'
import UploadsField from '@Fields/Uploads'
import AddressField from '@Fields/Address.vue'
import dataCountries from '@Data/countries'
import dataStates from '@Data/us-states'
import { ref, watch } from 'vue'

export default {
    components: {
        TextField,
        TextareaField,
        SelectField,
        CheckboxField,
        RadioField,
        DateField,
        UploadsField,
        AddressField,
    },

    props: ['modelValue', 'title', 'showEmail', 'isSpouse', 'series'],

    emits: ['update:modelValue', 'replicate'],

    setup(props, { emit }){
        const sameAsAbove = ref(false)

        watch(() => sameAsAbove.value, (state) => {
            if (state) {
                emit('replicate', 'secondary')
            }
        })

        watch(() => props.modelValue.financialAffiliation, (value) => {
            props.modelValue.financialFirm = (value == 'Yes') ? props.modelValue.employer : ''
            props.modelValue.financialFirmPosition = (value == 'Yes') ? props.modelValue.occupation : ''
        })

        watch(() => props.modelValue.publicCompanyAffiliation, (value) => {
            props.modelValue.publicCompany = (value == 'Yes') ? props.modelValue.employer : ''
            props.modelValue.publicCompanyPosition = (value == 'Yes') ? props.modelValue.occupation : ''
        })

        function onPlaceChanged(place){
            if (place.city) {
                props.modelValue.city = place.city
            }

            if (place.state) {
                props.modelValue.state = place.state
            }

            if (place.zip) {
                props.modelValue.zip = place.zip
            }

            if (place.country) {
                props.modelValue.country = place.country
            }
        }

        return {
            sameAsAbove,
            onPlaceChanged
        }
    },

    computed: {
        countries(){
            return dataCountries
        },
        states(){
            return dataStates;
        },
        salutations(){
            return [
                {
                    label: 'Dr.',
                    value: 'Dr'
                },
                {
                    label: 'Mrs.',
                    value: 'Mrs'
                },
                {
                    label: 'Ms.',
                    value: 'Ms'
                },
                {
                    label: 'Mr.',
                    value: 'Mr'
                },
                {
                    label: 'Other',
                    value: 'OtherSaluation'
                }
            ]
        }
    }
}
</script>

<style>

</style>
